import {
  Button,
  ButtonSize,
  ButtonVariant,
  Dialog,
  EmailInput,
  MaskedInput,
  Size,
  TextInput,
} from '@hourly-hds/components';
import { Smartphone } from '@hourly-hds/icons/glyph';
import { useState } from 'react';
import { z } from 'zod';
import styles from './NewReferralDialog.module.css';

const referralSchema = z
  .object({
    firstName: z.string().min(1, 'Please enter first name.'),
    lastName: z.string().min(1, 'Please enter last name.'),
    companyName: z.string().min(1, 'Please enter company legal name.'),
    email: z.email('Please enter a valid email.').optional().or(z.literal('')),
    mobileNumber: z
      .string()
      .regex(/^[2-9]\d{9}$/, 'Please enter a valid mobile number.')
      .optional()
      .or(z.literal('')),
    note: z.string(),
  })
  .refine((data) => data.email || data.mobileNumber, {
    message: 'Please enter a valid email.',
    path: ['email'],
  })
  .refine((data) => data.email || data.mobileNumber, {
    message: 'Please enter a valid mobile number.',
    path: ['mobileNumber'],
  });

type ReferralForm = z.infer<typeof referralSchema>;

const initialReferralForm: ReferralForm = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  mobileNumber: '',
  note: '',
};

const NewReferralDialog = ({
  isOpen,
  onClose,
  onFinish,
}: {
  isOpen: boolean;
  onClose: () => void;
  onFinish: (newReferral: ReferralForm) => void;
}) => {
  const [errors, setErrors] = useState<Partial<Record<keyof ReferralForm, string>>>({});
  const [referral, setReferral] = useState(initialReferralForm);

  const handleChangeReferral = (changes: Partial<ReferralForm>) => {
    setReferral((currentState) => ({ ...currentState, ...changes }));
  };

  const handleClose = () => {
    setReferral(initialReferralForm);
    setErrors({});
    onClose();
  };

  return (
    <div className={styles.overlayWrapper}>
      <Dialog
        open={isOpen}
        size={Size.Large}
        onClose={handleClose}
        aria-label="New Referral Dialog">
        <Dialog.Header>
          <div className={styles.header}>
            <span className={styles.title}>New Referral</span>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className={styles.form}>
            <div className={styles.twoCols}>
              <div className={styles.field}>
                <TextInput
                  label="First Name"
                  value={referral.firstName}
                  onChange={(firstName) => handleChangeReferral({ firstName })}
                  error={!!errors.firstName}
                  description={errors.firstName}
                />
              </div>
              <div className={styles.field}>
                <TextInput
                  label="Last Name"
                  value={referral.lastName}
                  onChange={(lastName) => handleChangeReferral({ lastName })}
                  error={!!errors.lastName}
                  description={errors.lastName}
                />
              </div>
            </div>

            <div className={styles.field}>
              <TextInput
                label="Company Name"
                value={referral.companyName}
                onChange={(companyName) => handleChangeReferral({ companyName })}
                error={!!errors.companyName}
                description={errors.companyName}
              />
            </div>

            <div className={styles.field}>
              <EmailInput
                label="Email"
                value={referral.email}
                onChange={(email) => handleChangeReferral({ email })}
                error={!!errors.email}
                description={errors.email}
              />
            </div>

            <div className={styles.field}>
              <MaskedInput
                label="Mobile Number"
                value={referral.mobileNumber}
                onChange={(mobileNumber) => handleChangeReferral({ mobileNumber })}
                mask="(000) 000 0000"
                icon={Smartphone}
                error={!!errors.mobileNumber}
                description={errors.mobileNumber}
              />
            </div>

            <div className={styles.field}>
              <TextInput
                label="Notes (Optional)"
                value={referral.note}
                onChange={(note) => handleChangeReferral({ note })}
              />
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button label="Cancel" onClick={handleClose} fullWidth size={ButtonSize.Large} />
          <Button
            label="Save"
            onClick={async () => {
              const result = referralSchema.safeParse(referral);
              if (!result.success) {
                const tree = z.treeifyError(result.error);
                const properties = tree.properties ?? {};

                setErrors({
                  firstName: properties?.firstName?.errors[0],
                  lastName: properties?.lastName?.errors[0],
                  companyName: properties?.companyName?.errors[0],
                  email: properties?.email?.errors[0],
                  mobileNumber: properties?.mobileNumber?.errors[0],
                });

                return;
              }

              await onFinish({ ...referral, note: referral.note.trim() });
              setReferral(initialReferralForm);
              setErrors({});
            }}
            variant={ButtonVariant.Primary}
            size={ButtonSize.Large}
            fullWidth
          />
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};

export default NewReferralDialog;
